<script setup>
import { computed, ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import {
  COMPONENT_TYPES,
  MEDIA_FORMATS,
  findComponentByType,
} from 'dashboard/helper/templateHelper';
import { splitVariables } from 'dashboard/helper/campaignVariables';

import Button from 'dashboard/components-next/button/Button.vue';
import Icon from 'dashboard/components-next/icon/Icon.vue';
import Spinner from 'dashboard/components-next/spinner/Spinner.vue';

// The message as it goes out. Until there is an audience it says where each variable comes from;
// with one, it shows the server's rendering for real people - the one the send uses - and who an
// empty variable leaves out.
const props = defineProps({
  // The template of a Cloud inbox, or null for an Evolution inbox, which sends `message` as text.
  template: { type: Object, default: null },
  message: { type: String, default: '' },
  // What fills each variable, keyed `section.key` ('body.1', 'message.contact.name'); empty while
  // the variable has no source yet.
  summaries: { type: Object, default: () => ({}) },
  preview: { type: Object, default: null },
  isLoading: { type: Boolean, default: false },
});

const { t } = useI18n();

const isText = computed(() => !props.template);

const component = type =>
  props.template ? findComponentByType(props.template, type) : null;

const header = computed(() => component(COMPONENT_TYPES.HEADER));
const mediaFormat = computed(() =>
  MEDIA_FORMATS.includes(header.value?.format)
    ? header.value.format.toLowerCase()
    : ''
);
const headerText = computed(() =>
  header.value?.format === 'TEXT' ? header.value.text : ''
);
const bodyText = computed(() =>
  isText.value ? props.message : component(COMPONENT_TYPES.BODY)?.text || ''
);
const footerText = computed(() => component('FOOTER')?.text || '');
const buttons = computed(
  () => component(COMPONENT_TYPES.BUTTONS)?.buttons || []
);

const samples = computed(() => props.preview?.samples || []);
const sampleIndex = ref(0);
const sample = computed(() => samples.value[sampleIndex.value] || null);

watch(samples, () => {
  sampleIndex.value = 0;
});

const hasContent = computed(() => !!bodyText.value.trim());

const PART_CLASSES = {
  source: 'bg-n-blue-3 text-n-blue-11',
  value: 'bg-n-blue-3 text-n-blue-11',
  empty: 'bg-n-ruby-3 text-n-ruby-11',
  unmapped: 'bg-n-amber-3 text-n-amber-11',
};

const toParts = (section, text) =>
  splitVariables(text).map(segment => {
    if (!('key' in segment)) return segment;

    const summary = props.summaries[`${section}.${segment.key}`];
    if (!summary) {
      return { kind: 'unmapped', text: `{{${segment.key}}}` };
    }
    if (!sample.value) return { kind: 'source', text: summary };

    const value = sample.value.processed_params?.[section]?.[segment.key];
    return value
      ? { kind: 'value', text: value }
      : { kind: 'empty', text: t('CAMPAIGN.WHATSAPP.CREATE.PREVIEW.EMPTY') };
  });

const headerParts = computed(() => toParts('header', headerText.value));

// The server renders a plain-text message whole, Liquid tags and all, so a contact's text is shown
// as it came back rather than cut at the variables again.
const bodyParts = computed(() => {
  if (isText.value && sample.value) return [{ text: sample.value.message }];
  return toParts(isText.value ? 'message' : 'body', bodyText.value);
});

const hasAudience = computed(() => !!props.preview);
const hasVariables = computed(() => Object.keys(props.summaries).length > 0);
</script>

<template>
  <div
    class="flex flex-col gap-4 p-5 border rounded-xl border-n-weak bg-n-solid-1"
  >
    <header class="flex items-center justify-between gap-2">
      <div class="flex items-center gap-2">
        <h2 class="text-base font-medium text-n-slate-12">
          {{ t('CAMPAIGN.WHATSAPP.CREATE.PREVIEW.TITLE') }}
        </h2>
        <Spinner v-if="isLoading" class="size-4 text-n-slate-11" />
      </div>
      <div
        v-if="samples.length > 1"
        class="flex items-center gap-1 text-xs text-n-slate-11"
      >
        <Button
          icon="i-lucide-chevron-left"
          color="slate"
          variant="ghost"
          size="xs"
          type="button"
          :disabled="sampleIndex === 0"
          @click="sampleIndex -= 1"
        />
        <span class="tabular-nums">
          {{ `${sampleIndex + 1} / ${samples.length}` }}
        </span>
        <Button
          icon="i-lucide-chevron-right"
          color="slate"
          variant="ghost"
          size="xs"
          type="button"
          :disabled="sampleIndex === samples.length - 1"
          @click="sampleIndex += 1"
        />
      </div>
    </header>

    <div
      v-if="!hasContent"
      class="flex flex-col items-center gap-2 px-4 py-10 text-center rounded-lg bg-n-alpha-black2"
    >
      <Icon icon="i-lucide-message-square" class="size-6 text-n-slate-10" />
      <p class="mb-0 text-sm text-n-slate-11">
        {{ t('CAMPAIGN.WHATSAPP.CREATE.PREVIEW.NOTHING_YET') }}
      </p>
    </div>

    <template v-else>
      <div class="flex items-center justify-between gap-2 text-xs">
        <span v-if="sample" class="truncate text-n-slate-11">
          {{
            t('CAMPAIGN.WHATSAPP.CREATE.PREVIEW.TO', {
              name: sample.contact.name || sample.contact.phone_number,
              phone: sample.contact.phone_number,
            })
          }}
        </span>
        <span v-else class="text-n-slate-11">
          {{ t('CAMPAIGN.WHATSAPP.CREATE.PREVIEW.SOURCES_HINT') }}
        </span>
        <span
          v-if="sample?.blank"
          class="px-1.5 py-0.5 rounded shrink-0"
          :class="
            isText
              ? 'bg-n-amber-3 text-n-amber-11'
              : 'bg-n-ruby-3 text-n-ruby-11'
          "
        >
          {{
            isText
              ? t('CAMPAIGN.WHATSAPP.CREATE.PREVIEW.GOES_WITH_GAP')
              : t('CAMPAIGN.WHATSAPP.CREATE.PREVIEW.SKIPPED')
          }}
        </span>
      </div>

      <div class="flex flex-col gap-1 p-3 rounded-lg bg-n-alpha-black2">
        <div
          class="flex flex-col gap-2 px-3 py-2 text-sm rounded-lg rounded-tr-sm bg-n-teal-3 text-n-slate-12"
        >
          <div
            v-if="mediaFormat"
            class="flex items-center gap-2 px-3 py-6 rounded-md bg-n-alpha-2 text-n-slate-11"
          >
            <Icon icon="i-lucide-image" class="size-4" />
            <span class="text-xs">
              {{
                t('CAMPAIGN.WHATSAPP.CREATE.PREVIEW.MEDIA', {
                  format: mediaFormat,
                })
              }}
            </span>
          </div>
          <p
            v-if="headerParts.length"
            class="mb-0 font-medium whitespace-pre-wrap break-words"
          >
            <template v-for="(part, index) in headerParts" :key="index">
              <span
                v-if="part.kind"
                class="px-0.5 rounded"
                :class="PART_CLASSES[part.kind]"
                >{{ part.text }}</span
              ><template v-else>{{ part.text }}</template>
            </template>
          </p>
          <p class="mb-0 whitespace-pre-wrap break-words">
            <template v-for="(part, index) in bodyParts" :key="index">
              <span
                v-if="part.kind"
                class="px-0.5 rounded"
                :class="PART_CLASSES[part.kind]"
                >{{ part.text }}</span
              ><template v-else>{{ part.text }}</template>
            </template>
          </p>
          <p v-if="footerText" class="mb-0 text-xs text-n-slate-11">
            {{ footerText }}
          </p>
        </div>
        <span
          v-for="(button, index) in buttons"
          :key="index"
          class="py-1.5 text-xs text-center rounded-lg bg-n-solid-2 text-n-blue-11"
        >
          {{ button.text }}
        </span>
      </div>
    </template>

    <div
      v-if="hasAudience"
      class="flex flex-col gap-1 pt-3 text-sm border-t border-n-weak"
    >
      <span class="text-n-slate-12">
        {{
          t(
            'CAMPAIGN.WHATSAPP.CREATE.PREVIEW.REACH',
            { count: preview.total },
            preview.total
          )
        }}
      </span>
      <span v-if="preview.blank_count" class="text-n-amber-11">
        {{
          t(
            isText
              ? 'CAMPAIGN.WHATSAPP.CREATE.PREVIEW.BLANK_TEXT'
              : 'CAMPAIGN.WHATSAPP.CREATE.PREVIEW.BLANK_TEMPLATE',
            { count: preview.blank_count },
            preview.blank_count
          )
        }}
      </span>
      <span v-else-if="preview.scanned && hasVariables" class="text-n-teal-11">
        {{ t('CAMPAIGN.WHATSAPP.CREATE.PREVIEW.ALL_FILLED') }}
      </span>
      <span
        v-if="preview.scanned < preview.total"
        class="text-xs text-n-slate-11"
      >
        {{
          t('CAMPAIGN.WHATSAPP.CREATE.PREVIEW.SCANNED', {
            count: preview.scanned,
          })
        }}
      </span>
    </div>
    <p v-else-if="hasContent" class="mb-0 text-xs text-n-slate-11">
      {{ t('CAMPAIGN.WHATSAPP.CREATE.PREVIEW.NO_AUDIENCE') }}
    </p>
  </div>
</template>
